import { useMemo, useState } from "react";
import { ErrorPanel, Icon, Skeleton, Typography } from "@/components/atoms";
import { DataTable, EmptyState, Pagination } from "@/components/molecules";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useOperator, usePagination, useSorting } from "@/hooks";
import { useGetVehiclesQuery } from "@/redux/apis/vehicle/vehicleApi";
import { affiliateVehiclesColumns } from "../AffiliateVehicles/fixtures";
import { generateAffiliateVehicleTableData } from "../AffiliateVehicles/helpers";
import { useGetAffiliateVehiclesParams } from "../AffiliateVehicles/hooks/useGetAffiliateVehiclesParams";
import { DriversVehicleMovedModal } from "./modals/DriversVehicleMovedModal";

// Read-only columns — exclude Actions
const readOnlyColumns = affiliateVehiclesColumns.filter((col) => col.id !== "actions");

export const LegacyAffiliateVehiclesPanel = () => {
  const { id: operatorId } = useOperator();
  const { params } = useGetAffiliateVehiclesParams();
  const { sorting, setSorting } = useSorting();
  const { handlePageClick, handleSizeChange } = usePagination("affiliate-vehicles", 25);
  const { isFetching, data: response, error } = useGetVehiclesQuery(params, { refetchOnMountOrArgChange: true });
  const [showModal, setShowModal] = useState(true);

  const data = useMemo(
    () => {
      if (!response) return [];
      return generateAffiliateVehicleTableData(operatorId, response.vehicles, () => {}, () => {}, () => {});
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [response?.vehicles, operatorId]
  );

  if (error) return <ErrorPanel messages={getErrorMessages(error)} />;
  if (isFetching) return <Skeleton />;

  if (response && response.vehicles.length > 0)
    return (
      <>
        <div className="mb-3 mt-4 flex items-center justify-center gap-2 rounded-lg border border-info bg-info/5 px-4 py-3">
          <Icon name="LampCharge" size="sm" className="shrink-0 text-info" />
          <Typography variant="paragraph" className="text-info">
            This is view-only mode. My Driver&apos;s Vehicles are now managed in the new Vehicle Manager.
          </Typography>
        </div>
        <DataTable
          className="w-full overflow-x-auto [&_table]:table-auto [&_tr]:bg-white"
          data={data}
          columns={readOnlyColumns}
          sorting={sorting}
          setSorting={setSorting}
        />
        {response.page && (
          <Pagination
            onPageClick={handlePageClick}
            onSizeChange={handleSizeChange}
            currentPage={response.page.current}
            pageSize={response.page.size}
            totalItems={response.page.total}
            pageCount={response.page.count}
          />
        )}
        <DriversVehicleMovedModal open={showModal} onClose={() => setShowModal(false)} />
      </>
    );

  return (
    <>
      <EmptyState title="No Driver Vehicles" description="There are no driver vehicles to display." />
      <DriversVehicleMovedModal open={showModal} onClose={() => setShowModal(false)} />
    </>
  );
};
